/* global AppSettings */
/**
 * Size guide
 *
 * @author Andy Lemaire
 */
import $ from 'jquery';
import Mustache from 'mustache';
import createLogger from 'components/logger/Logger';
import AnalyticsEventTypes from 'components/analytics/AnalyticsEventTypes';
import EventTypes from 'components/EventTypes';
import scrollTo from 'components/utils/scrollTo';
import Singleton from 'components/utils/Singleton';
import device from 'components/utils/device';
import SizeGuideKeys from './sizeGuideKeys';
import { gsap, Power2, CSSPlugin } from 'gsap/all';
import 'jquery.resizeEvents';
import 'maxZindex';
import requestIdleCallback from '../utils/requestIdleCallback';

const Logger = createLogger('sizeGuide');
gsap.registerPlugin(Power2, CSSPlugin);

const clickEvent = 'click touch';
const isActiveSelector = 'is-active';
const isActiveClass = `.${isActiveSelector}`;
const isHiddenSelector = 'is-hidden';
const isOpenedSelector = 'is-opened';
const isSelectedSelector = 'is-selected';
const isSelectedClass = `.${isSelectedSelector}`;
const isLastActiveElement = 'last';
const makeSelectionClass = '.js-makeSelection';
const modelMeasurementSpotClass = '.js-modelMeasurement-spot';
const modelImageWrapClass = '.js-modelImageWrap';
const navPanelClass = '.js-navPanel';

const generateSizeSelectionListKeys = [
  SizeGuideKeys.GENDER,
  SizeGuideKeys.GUIDE,
  SizeGuideKeys.UNIT,
];
const measurementSpotKeys = [
  SizeGuideKeys.CHEST,
  SizeGuideKeys.WAIST,
  SizeGuideKeys.HIP,
  SizeGuideKeys.INSEAM,
  SizeGuideKeys.FOOT,
  SizeGuideKeys.LENGTH,
];
const numberedSizeRanges = [
  `${SizeGuideKeys.SIZERANGE}:${SizeGuideKeys.SIZE}`,
  `${SizeGuideKeys.SIZERANGE}:${SizeGuideKeys.SIZEWAIST}`,
];
const optionalSelectionSegments = [SizeGuideKeys.WAIST, SizeGuideKeys.LENGTH, SizeGuideKeys.SIZE];
const resetMeasurementSelectionKeys = [
  SizeGuideKeys.GENDER,
  SizeGuideKeys.GUIDE,
  SizeGuideKeys.SIZERANGE,
];

const measurementSpotTemplate =
  '<div data-spot="{{spot}}" class="sizeGuide-modelMeasurement-spot js-modelMeasurement-spot"><i class="measurementsInfoIcon sizeGuide-modelMeasurement-spotIcon"></i><span class="sizeGuide-modelMeasurement-spotLabel js-spotLabel">{{label}}</span><span class="sizeGuide-modelMeasurement-spotValue js-spotValue" data-display-value="{{spot}}">...</span></div>';
const sizeSelectListItemTemplate =
  '<li class="sizeGuide-sizeSelect-listItem{{#large}} sizeGuide-sizeSelect-listItem--large{{/large}}{{#selected}} is-selected{{/selected}} js-makeSelection{{#dummy}} sizeGuide-sizeSelect-listItem--dummy{{/dummy}}" {{^dummy}}data-select="{{key}}:{{val}}"{{/dummy}}>{{label}}</li>';

const activeSelection = {};
const addLocalizedSizesEnabled = false;
const localizedSizePtrn = /\s*-.+/;
let scrollPositionY;
let top;

function SizeGuide() {
  this.initialize();
}

$.extend(SizeGuide.prototype, {
  initialize(element, layerData) {
    this.sizeGuide = document.querySelector('.js-sizeGuide');
    this.modelImageWrap = this.sizeGuide.querySelector(modelImageWrapClass);
    this.modelMeasurementSpots = this.sizeGuide.querySelectorAll(modelMeasurementSpotClass);
    this.navItems = [...this.sizeGuide.querySelectorAll('.js-sizeSelectNavItem')];
    this.selectionElements = this.sizeGuide.querySelectorAll('[data-select]');
    this.sizeSelections = this.sizeGuide.querySelectorAll('.js-navPanel--sizeSelection');
    this.tables = [...this.sizeGuide.querySelectorAll('.js-measurementsTable')];
    this.$dialog = $('.js-dialog');
    this.sizeGuideTrigger = document.querySelector('.js-pageLayerTrigger.js-sizeGuideTrigger');
    this.currentCountryCode = AppSettings.country.toLowerCase();

    // Make the bottoms waist table function as an individual sizeRange for bottom single sizes (skirts, trousers)
    $('.js-measurementsTable.js-measurementsTable--wl.js-measurementsTable--waist').addClass(
      'js-measurementsTable--sizeWaist'
    );

    this.navItems.forEach(navItem =>
      navItem.setAttribute('data-originalLabel', navItem.textContent)
    );

    this.bindEvents();
    this.addLocalizedSizes();
    this.translateTableHeadings();
    this.modelMeasurementSpots.forEach(modelMeasurementSpot =>
      this.translateSpotLabel(modelMeasurementSpot)
    );
    this.generateMeasurementSpots();

    const url = this.sizeGuideTrigger ? this.sizeGuideTrigger.href : window.location.search;
    const params = this.getParams(url);
    if (Object.keys(params).length) {
      this.selectParams(params);
    } else {
      const firstGenderLink = this.sizeGuide.querySelector('.js-navPanel--gender .js-navPanelLink');
      const firstCategoryLink = this.sizeGuide.querySelector(
        '.js-navPanel--category .js-navPanelLink'
      );

      firstGenderLink.click();
      firstCategoryLink.click();
    }

    const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
    this.toggleAudience(genderValue);

    this.setPreferredUnit();
    this.updateSizeInfo();

    this.selectActiveMeasurements();
    this.highlightSelectedTableRow();
    this.isInitialized = false;
    this.displayMeasurementValuesOnModelImage(false, true);

    scrollTo(0);

    document.jq.trigger(EventTypes.SIZE_GUIDE_INITIALIZATION_COMPLETE);

    const sku = document.querySelector('.js-productDetailSection')?.dataset?.sku;

    if (this.sizeGuide.closest('.js-dialog') && sku) {
      window.setTimeout(() => {
        document.jq.trigger(EventTypes.DIALOG_SIZE_GUIDE_SHOW);
        document.jq.trigger(AnalyticsEventTypes.SIZE_GUIDE_OPEN, {
          materialNumber: sku,
        });
      }, 500);
    }
  },

  updateSizeInfo() {
    // Show correct size tab
    if (
      this.getKeyValue(SizeGuideKeys.GUIDE) === SizeGuideKeys.BOTTOMS &&
      this.getUrlParam(SizeGuideKeys.SIZE)
    ) {
      this.setActiveKeyValue('waist', this.getUrlParam(SizeGuideKeys.SIZE));

      if (
        [SizeGuideKeys.BOYS, SizeGuideKeys.GIRLS, SizeGuideKeys.BABIES].includes(
          this.getUrlParam(SizeGuideKeys.GENDER)
        )
      ) {
        this.selectElement('sizeRange', 'wl');
        this.setActiveKeyValue('sizeRange', 'wl');

        return;
      }

      this.selectElement('sizeRange', 'sml');
      this.setActiveKeyValue('sizeRange', 'sml');
    } else if (
      [SizeGuideKeys.SHOES, SizeGuideKeys.BELTS].includes(this.getKeyValue(SizeGuideKeys.GUIDE))
    ) {
      this.selectElement('sizeRange', 'size');
      this.setActiveKeyValue('sizeRange', 'size');
    } else if (this.getKeyValue(SizeGuideKeys.GUIDE) === SizeGuideKeys.TOPS) {
      this.selectElement('sizeRange', 'sml');
      this.setActiveKeyValue('sizeRange', 'sml');
    }
  },

  bindEvents() {
    this.sizeGuide.jq
      .on(clickEvent, makeSelectionClass, this.clickElement.bind(this))
      .on(clickEvent, '.js-navPanelLink', this.togglePanel.bind(this))
      .on(clickEvent, modelMeasurementSpotClass, this.showSpotInfo.bind(this))
      .on(clickEvent, '.js-spotInfoTrigger', this.showSpotFromTable.bind(this))
      .on(clickEvent, '.js-closeInfo', this.hideSpotInfo.bind(this));

    document.jq
      .on(EventTypes.DIALOG_CLOSE, () => {
        window.jq.off('.sizeGuide');
        this.isInitialized = false;
      })
      .on(EventTypes.DIALOG_SHOW, event => {
        this.setData();
        requestIdleCallback(this.handleAnalytics.bind(this, event));
      });
  },

  /**
   * Add localized size labels to measurement data
   */
  addLocalizedSizes() {
    if (addLocalizedSizesEnabled) {
      let mappingTableCellIndex;

      this.tables.jq.each(() => {
        // Extract className for corresponding mapping table. Don't include unit selection value
        const classNames = this.tables.jq
          .attr('class')
          .replace(/js-measurementsTable(--(cm|inch)?)?(\s)?/gi, '')
          .split(' ');

        const mapTableSelector = classNames
          .map(name => {
            return `.js-mappingTable--${name}`;
          })
          .join('');

        const $correspondingMapTable = this.sizeGuide.jq.find(mapTableSelector);

        // retrieve the tr > td mappingTableCellIndex of mapping value for current locale
        $correspondingMapTable.find('tr:first td').each((i, element) => {
          const $tableCell = $(element);
          this.tableCellText = $tableCell.text();
          this.countryCode = $.trim(this.tableCellText.toLowerCase());

          if (this.countryCode === this.currentCountryCode) {
            mappingTableCellIndex = i;
          }
        });

        // Filter size mapping tables for measurement tables with reference values (s, m, l || 32, 34, 26, ...)
        const mapping = {};

        // Add values in table cell found at mappingTableCellIndex
        $correspondingMapTable.find('tr').each((i, element) => {
          const $tableRow = $(element);
          const $tableCells = $tableRow.children('td');
          const size = $.trim($tableCells.eq(0).text().toLowerCase());

          mapping[size] = $.trim($tableCells.eq(mappingTableCellIndex).text().toLowerCase());
        });

        // Loop size tables and add mapped size
        this.tables.jq.find('tr:not(:first)').each((i, element) => {
          const $tableRow = $(element);
          const $tableCells = $tableRow.children('td');
          this.$sizeTableCells = $tableCells.eq(0);
          const size = $.trim(this.$sizeTableCells.text().toLowerCase());

          this.$sizeTableCells.data('size', size);

          if (mapping[size] && mapping[size] !== size) {
            this.newLabel = `${size} - ${this.currentCountryCode} ${mapping[size]}`;

            this.$sizeTableCells.text(this.newLabel);
          }
        });
      });
    }
  },

  /**
   * Animate measurement spot
   */
  animateMeasurementSpot(spotValue, fromSize, toSize, shouldAnimateCounter) {
    const modelMeasurementSpot = spotValue.closest(modelMeasurementSpotClass);

    if (fromSize === toSize) {
      return false;
    }

    modelMeasurementSpot.style.borderBottomColor = '#303030';

    gsap.to(modelMeasurementSpot, {
      duration: 0.6,
      css: { borderBottomColor: '#ddd' },
      delay: 0.2,
    });

    if (this.isInitialized || (!this.isInitialized && isNaN(fromSize) && shouldAnimateCounter)) {
      fromSize = parseFloat(toSize) - 5;
      this.animateCounter(spotValue, fromSize, toSize);
    }
  },

  /**
   * Animate counter effect
   */
  animateCounter(spotValue, fromSize, toSize) {
    const counter = { size: parseFloat(fromSize) };
    let fraction = '';

    if (activeSelection.unit === SizeGuideKeys.INCH) {
      const fractionQuery = toSize.split(' ');

      if (fractionQuery[1]) {
        fraction = `${fractionQuery[1]}`;
        if (fraction) {
          fraction = ` ${fraction}`;
        }
      }
    }

    gsap.to(counter, {
      duration: 0.6,
      size: parseFloat(toSize),
      onUpdate() {
        spotValue.textContent = `${Math.ceil(counter.size * 10) / 10 + fraction} ${
          activeSelection.unit
        }`;
      },
      ease: Power2.easeOut,
    });
  },

  classMatchesWaistLengthPart(className) {
    const wlReg = /(waist|length|sizeWaist)/;
    const rangePartMatches = className.match(wlReg);
    return rangePartMatches ? rangePartMatches[0] : false;
  },

  /**
   * Click element
   */
  clickElement(event) {
    event.preventDefault();
    const query = event.currentTarget.dataset.select;

    if (!query) {
      return;
    }

    const selectQuery = query.split(':');
    const key = selectQuery[0];
    const value = selectQuery[1];

    if (key === SizeGuideKeys.GENDER) {
      this.toggleAudience(value);
    }

    const generateSizeSelectionList = generateSizeSelectionListKeys.indexOf(key) > -1;
    const isSizeSelectKey = optionalSelectionSegments.indexOf(key) > -1;
    const resetMeasurementSelection = resetMeasurementSelectionKeys.indexOf(key) > -1;

    if (resetMeasurementSelection) {
      this.resetMeasurementSelection();
    }

    if (key === SizeGuideKeys.GENDER || key === SizeGuideKeys.GUIDE || key === SizeGuideKeys.UNIT) {
      this.setParams(key, value);
      const url = this.sizeGuideTrigger ? this.sizeGuideTrigger.href : window.location.search;
      const params = this.getParams(url);
      this.selectParams(params);
      this.isInitialized = false;
    } else {
      this.selectElement(key, value);
      this.setActiveKeyValue(key, value);
    }

    if (key === SizeGuideKeys.SIZERANGE) {
      this.isInitialized = false;
    }

    // Remove selected sizes so they don't transfer to a different guide
    if (!isSizeSelectKey && key !== SizeGuideKeys.UNIT) {
      optionalSelectionSegments.forEach(selectedKey => delete activeSelection[selectedKey]);
    }

    // Preselect current activeSelection (not visible)
    if (generateSizeSelectionList || key === SizeGuideKeys.UNIT) {
      this.setActiveTables();
    }

    if (generateSizeSelectionList) {
      this.generateSizeRangeSelection();
    }

    this.selectActiveMeasurements();
    this.highlightSelectedTableRow();
    this.loadModelImage();
    const shouldRefreshMeasurementSpots = measurementSpotKeys.reduce(
      (shouldRefreshMeasurementSpots, measurementSpotKey, i) => {
        return shouldRefreshMeasurementSpots && key !== measurementSpotKey;
      },
      true
    );
    this.displayMeasurementValuesOnModelImage(
      isSizeSelectKey ? key : false,
      shouldRefreshMeasurementSpots
    );

    if (key === SizeGuideKeys.GENDER || key === SizeGuideKeys.GUIDE) {
      this.updateCategory();
    }

    document.jq.trigger(AnalyticsEventTypes.ARTIFICIAL_PAGE_VIEW);
  },

  /**
   * Displays measurements retrieved from selected table data on the info spot
   */
  displayMeasurementValuesOnModelImage(sizeRangeType, shouldRefreshMeasurementSpots) {
    const modelMeasurementSpots = this.sizeGuide.querySelectorAll(modelMeasurementSpotClass);
    if (shouldRefreshMeasurementSpots) {
      modelMeasurementSpots.forEach(modelMeasurementSpot =>
        modelMeasurementSpot.classList.remove(isActiveSelector)
      );
    }

    modelMeasurementSpots.jq
      .find(`[data-display-value="${SizeGuideKeys.UNIT}"]`)
      .text(`${this.getKeyValue(SizeGuideKeys.UNIT)}`);
    this.sizeRangeType = sizeRangeType;

    const selectedTables = this.tables.filter(table =>
      table.classList.contains(isSelectedSelector)
    );
    if (selectedTables.length) {
      selectedTables.forEach(table => this.processMeasurementTable(table));
      this.isInitialized = true;
    }

    if (this.modelImageWrap && this.sizeRangeType) {
      this.modelImageWrap.jq
        .css({
          opacity: 0.4,
        })
        .delay(60)
        .animate(
          {
            opacity: 1,
          },
          900
        );
    }
  },

  /**
   * Get parameters from url
   */
  getParams(url) {
    const params = {};
    const searchParams = url.split('?');
    const query = searchParams.pop();
    if (query) {
      query.split('&').forEach(pair => {
        const keyValue = pair.split('=');
        const key = keyValue[0];
        const value = keyValue[1];
        if (key && value) {
          params[key] = decodeURIComponent(value);
        }
      });
    }
    return params;
  },

  /**
   * Set parameters in url or sizeGuide trigger
   */
  setParams(key, value) {
    const url = this.sizeGuideTrigger ? this.sizeGuideTrigger.href : window.location.search;
    const params = this.getParams(url);
    params[key] = value;
    const queryString = Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&');

    if (!this.sizeGuideTrigger) {
      const baseUrl = window.location.href.split('?')[0];
      window.history.replaceState({}, '', `${baseUrl}?${queryString}`);
    } else {
      this.sizeGuideTrigger.setAttribute(
        'href',
        `/${AppSettings.locale}/size-guide?${queryString}`
      );
    }
  },

  /**
   * Select parameters
   */
  selectParams(params) {
    for (const key in params) {
      if (params[key]) {
        this.selectElement(key, params[key]);
        this.setActiveKeyValue(key, params[key]);
      }
    }
    this.updateCategory();
    this.loadModelImage();
  },

  /**
   * Process specific measurement table
   */
  processMeasurementTable(table) {
    let shouldAnimateCounter = true;
    let selectedRow = [...table.rows].filter(row => row.classList.contains(isSelectedSelector));
    if (!selectedRow.length && this.isInitialized) {
      return;
    }

    if (!selectedRow.length && !this.isInitialized) {
      shouldAnimateCounter = false;
      selectedRow = [table.rows[0]];
    }

    this.modelMeasurementSpots = this.sizeGuide.querySelectorAll(modelMeasurementSpotClass);

    const firstRow = table.querySelector('tr');
    const firstRowCells = Array.from(firstRow.cells);
    firstRowCells.shift();

    const spotKeys = firstRowCells.map(cell => {
      const spotKey = cell.dataset.originalValue;
      this.modelMeasurementSpots.forEach(modelMeasurementSpot => {
        if (modelMeasurementSpot.dataset.spot === spotKey) {
          modelMeasurementSpot.classList.add(isActiveSelector);
        }
      });
      return spotKey;
    });
    const tableCellsExceptFirst = Array.from(selectedRow[0].cells);
    tableCellsExceptFirst.shift();

    if (tableCellsExceptFirst) {
      tableCellsExceptFirst.forEach((cell, i) =>
        this.showMeasurementCounter(cell, i, spotKeys, shouldAnimateCounter)
      );
    }
  },

  /**
   * Show measurement counter
   */
  showMeasurementCounter(element, i, spotKeys, shouldAnimateCounter) {
    const spotKey = spotKeys[i];
    const activeSpot = [...this.modelMeasurementSpots].filter(
      modelMeasurementSpot => modelMeasurementSpot.dataset.spot === spotKey
    );

    if (!activeSpot.length) {
      return;
    }

    const sizeValue = element.textContent;
    const spotValue = activeSpot[0].querySelector(`[data-display-value="${spotKey}"]`);
    let fromSize = spotValue.textContent;
    const toSize = sizeValue;

    this.animateMeasurementSpot(spotValue, fromSize, toSize, shouldAnimateCounter);
  },

  /**
   * Generate info spot above the model image
   */
  generateMeasurementSpots() {
    measurementSpotKeys.forEach(this.generateInfoSpotHtml.bind(this));
  },

  /**
   * Generate info spot HTML
   */
  generateInfoSpotHtml(spot) {
    const spotData = {
      spot,
      label: window.spotLabelTranslations[spot] || spot,
    };

    const $sizeGuideModel = this.sizeGuide.jq.find('.js-sizeGuideModel');
    $sizeGuideModel.append(Mustache.render(measurementSpotTemplate, spotData));
  },

  processTable(table) {
    // Check if measurement table(s), not mapping table(s)
    const sizeRangeTypeReg = /(sml|wl|sizeWaist|size)/gi;
    const sizeRanges = table.className.match(sizeRangeTypeReg);
    this.sizeSelectKey = SizeGuideKeys.SIZE;

    // Select sizeRange table(s), not mapping table(s)
    if (sizeRanges.length) {
      const sizeRangeSelector = sizeRanges
        .map(sizeRange => {
          return `[data-select="${SizeGuideKeys.SIZERANGE}:${sizeRange}"]`;
        })
        .join(',');

      const activeNavItems = this.navItems.filter(navItem => navItem.matches(sizeRangeSelector));
      if (activeNavItems.length) {
        activeNavItems.forEach(activeNavItem => activeNavItem.classList.add(isActiveSelector));
      }

      let currentSizeSelectListClass = '.js-sizeGuide-sizeSelect-list';

      // When a sizeRangeType === waist x length (wl), the selection consists of 2 types of lists (waist and length)
      if (sizeRanges.indexOf('wl') !== -1) {
        const rangePartMatch = this.classMatchesWaistLengthPart(table.className);
        if (rangePartMatch) {
          currentSizeSelectListClass += `[data-range-part="${rangePartMatch}"]`;
          this.sizeSelectKey = rangePartMatch;
        }
      }

      const sizeSelectLists = [...this.sizeGuide.querySelectorAll('.js-sizeSelectLists')];
      const filteredSizeSelectLists = sizeSelectLists.filter(sizeSelectList =>
        sizeSelectList.matches(sizeRangeSelector)
      );
      const lists = filteredSizeSelectLists.map(filteredList =>
        filteredList.querySelectorAll(currentSizeSelectListClass)
      );

      if (!lists.length) {
        return;
      }

      this.sizesListed = [];
      const tableRows = Array.from(table.querySelectorAll('tr'));
      tableRows.shift();

      lists.forEach(list => {
        list.innerHTML = '';
        this.selectedSize = this.getKeyValue(this.sizeSelectKey);

        const listData = [];
        tableRows.forEach(row => listData.push(this.generateListItem(list, row)));

        // reset attributes when value is not available while switching gender or category
        const hasSelectedItem = listData.some(data => data.selected);
        if (!hasSelectedItem) {
          this.setActiveKeyValue(this.sizeSelectKey, null);
        }

        // Add dummy to even height of columns
        if (list.length % 2 === 1) {
          const dummyListItem = Mustache.render(sizeSelectListItemTemplate, {
            dummy: true,
            label: '--',
          });
          list.jq.append(dummyListItem);
        }

        const listSection = list.jq.closest('.js-sizeSelectLists').get(0);
        const query = listSection.dataset.select;

        // If sizeRangeType is "size" generate dynamic label of the first 2 size entries:  34, 36, ... or 56, 58, ...
        if (numberedSizeRanges.indexOf(query) !== -1 && this.sizesListed.length) {
          let label = this.sizesListed.slice(0, 2);
          label.push('...');
          label = label.join(', ');

          activeNavItems.forEach(activeNavItem => {
            if (activeNavItem.dataset.select === query) {
              const activeNavItemSpans = activeNavItem.querySelectorAll('span');
              activeNavItemSpans.forEach(span => (span.innerHTML = label));
            }
          });
        }
      });
    }
  },

  /**
   * Generates size selection [*] 40, [ ] 42 lists that can be used for displaying a selected size.
   * This function also activates the corresponding navigation button (s, m, l - 48, 50, ... etc.)
   * on the measurement spots
   */
  generateSizeRangeSelection() {
    const $sizeSelectList = this.sizeGuide.jq.find('.js-sizeGuide-sizeSelect-list');

    // Empty generated list element(s)
    $sizeSelectList.empty();

    // Reset navItems selection
    this.navItems.forEach(navItem => {
      navItem.classList.remove(isActiveSelector);
      navItem.classList.remove(isSelectedSelector);
      navItem.classList.remove(isLastActiveElement);
    });

    // Loop selected table data and generate size selection list element(s)
    const activeTables = this.tables.filter(table => table.classList.contains(isActiveSelector));
    activeTables.forEach(activeTable => this.processTable(activeTable));

    $(`.js-sizeSelectNavItem.${isActiveSelector}`).last().addClass(isLastActiveElement);

    const activeSizeRange = this.sizeSelections.jq.find(isActiveClass).first().attr('data-select');

    if (activeSizeRange) {
      const query = activeSizeRange.split(':');
      const value = query[1];
      this.setActiveKeyValue(SizeGuideKeys.SIZERANGE, value);
    }

    // Reselect selection element(s)
    this.selectionElements = this.sizeGuide.querySelectorAll('[data-select]');
  },

  /**
   * Generate specific list item
   */
  generateListItem(list, row) {
    const wideListReg = /-/;
    const firstTableCell = row.querySelector('td');
    const sizeRaw = firstTableCell.jq.data(SizeGuideKeys.SIZE) || firstTableCell.textContent;
    const size = sizeRaw.toLowerCase().replace(localizedSizePtrn, '');
    const isWideList = (size || '').match(wideListReg) ? true : false;

    const listItemData = {
      key: this.sizeSelectKey,
      val: size,
      label: size,
      selected: size === this.selectedSize,
      large: isWideList,
    };

    this.sizesListed.push(size);
    const listItem = Mustache.render(sizeSelectListItemTemplate, listItemData);
    list.jq.append(listItem);
    list.jq.toggleClass('sizeGuide-sizeSelect-list--large', isWideList);

    return listItemData;
  },

  /**
   * Get value of key
   */
  getKeyValue(key) {
    if (key === SizeGuideKeys.GENDER || key === SizeGuideKeys.GUIDE || key === SizeGuideKeys.UNIT) {
      return this.getUrlParam(key);
    }

    return this.sizeGuide.getAttribute(`data-active-${key}`);
  },

  getUrlParam(key) {
    const url = this.sizeGuideTrigger ? this.sizeGuideTrigger.href : window.location.search;
    const params = this.getParams(url);

    return params[key];
  },

  /**
   * Hide spot info
   */
  hideSpotInfo(event) {
    if (event) {
      event.stopPropagation();
    }

    // Timeout to prevent clicking the underlaying measurement spot when closing the spot info
    if (device.hasTouch) {
      setTimeout(this.hideSpotInfoAction(), 400);
    } else {
      this.hideSpotInfoAction();
    }
  },

  hideSpotInfoAction() {
    this.sizeGuide.classList.remove('has-activeInfo');
    this.sizeGuide.setAttribute('data-active-spot', null);
    const modelMeasurementInfo = this.sizeGuide.querySelector('.js-modelMeasurementInfo');
    const modelMeasurementInfoParagraphs = modelMeasurementInfo.querySelectorAll('p');
    if (modelMeasurementInfoParagraphs.length) {
      modelMeasurementInfoParagraphs.forEach(paragraph =>
        paragraph.classList.remove(isActiveSelector)
      );
    }

    if (scrollPositionY) {
      this.$dialog.scrollTop(scrollPositionY);
      scrollPositionY = undefined;
    }
  },

  /**
   * Highlight selected table row
   */
  highlightSelectedTableRow() {
    const selectedTables = this.tables.filter(table =>
      table.classList.contains(isSelectedSelector)
    );
    selectedTables.forEach(selectedTable => this.processTableRow(selectedTable));
  },

  /**
   * Process specific table row
   */
  processTableRow(table) {
    const activeSize = this.getKeyValue(SizeGuideKeys.SIZE);
    const activeWaist = this.getKeyValue(SizeGuideKeys.WAIST);
    const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
    const isNotAChild = ![SizeGuideKeys.BABIES, SizeGuideKeys.GIRLS, SizeGuideKeys.BOYS].includes(
      genderValue
    );

    const tableIsWaistLength = table.jq.is('.js-measurementsTable--wl');
    const tableIsWaist = table.jq.is('.js-measurementsTable--waist');

    let requestedSizeValue = activeSize;

    if (isNotAChild) {
      if (tableIsWaistLength) {
        requestedSizeValue = tableIsWaist ? activeWaist : this.getKeyValue(SizeGuideKeys.LENGTH);
      }
    } else if (tableIsWaistLength && tableIsWaist) {
      requestedSizeValue = activeWaist;
    }

    const tableRows = Array.from(table.querySelectorAll('tr'));
    tableRows.shift();

    tableRows.forEach(row => {
      const cell = row.children[0];
      const tableCellSizeRaw = cell.jq.data(SizeGuideKeys.SIZE) || cell.textContent.toLowerCase();
      const tableCellSize = tableCellSizeRaw.replace(localizedSizePtrn, '');

      row.classList.remove(isSelectedSelector);

      if (tableCellSize === requestedSizeValue) {
        row.classList.add(isSelectedSelector);
      }
    });
  },

  /**
   * Load model SVG image
   */
  loadModelImage() {
    const deferred = new $.Deferred();
    const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
    const guideValue = this.getKeyValue(SizeGuideKeys.GUIDE);
    let id = genderValue;

    const sizeGuideModelWrap = this.sizeGuide.querySelector('.js-sizeGuideModelWrap');
    if (genderValue === SizeGuideKeys.MEN || genderValue === SizeGuideKeys.WOMEN) {
      this.sizeSelections.forEach(sizeSelection => {
        sizeSelection.classList.remove(isHiddenSelector);
        sizeSelection.setAttribute('aria-expanded', true);
        sizeSelection.setAttribute('aria-hidden', false);
      });

      sizeGuideModelWrap.classList && sizeGuideModelWrap.classList.remove(isHiddenSelector);
      sizeGuideModelWrap.setAttribute('aria-expanded', true);
      sizeGuideModelWrap.setAttribute('aria-hidden', false);
    } else {
      this.sizeSelections.forEach(sizeSelection => {
        sizeSelection.classList.add(isHiddenSelector);
        sizeSelection.setAttribute('aria-expanded', false);
        sizeSelection.setAttribute('aria-hidden', true);
      });

      sizeGuideModelWrap.classList && sizeGuideModelWrap.classList.add(isHiddenSelector);
      sizeGuideModelWrap.setAttribute('aria-expanded', false);
      sizeGuideModelWrap.setAttribute('aria-hidden', true);
      return;
    }

    if (guideValue === SizeGuideKeys.BELTS || guideValue === SizeGuideKeys.SHOES) {
      id = guideValue;
    }

    const fileName = `model-${id}.svg`;

    this.modelImageWraps = [...this.sizeGuide.querySelectorAll(modelImageWrapClass)];
    if (this.modelImageWraps.length) {
      this.modelImageWraps.forEach(modelImageWrap =>
        modelImageWrap.classList.remove(isActiveSelector)
      );
    }

    const existingModelImageWrap = this.modelImageWraps.filter(
      modelImageWrap => modelImageWrap.dataset.name === fileName
    );

    if (id && existingModelImageWrap.length === 0) {
      const wrapper = document.createElement('div');
      wrapper.className = 'js-modelImageWrap sizeGuide-modelImageWrap is-active';
      wrapper.dataset.name = fileName;
      const sizeGuideModel = this.sizeGuide.querySelector('.js-sizeGuideModel');

      if (!sizeGuideModel) {
        return;
      }

      sizeGuideModel.appendChild(wrapper);

      $.ajax({
        url: `${AppSettings.assetPath}img/sizeguide/${fileName}`,
        dataType: 'xml',
        success(svg) {
          let svgElement;

          try {
            svgElement = svg.documentElement;
          } catch (error) {}

          wrapper.appendChild(svgElement);
          deferred.resolve();

          // Complete removal and re-adding is necessary for some webkit browsers which seem to have
          // issues with finding classnames and render the appropriate styles associated with them
          requestAnimationFrame(() => {
            const html = wrapper.innerHTML;
            wrapper.innerHTML = '';
            wrapper.innerHTML = html;
          });
        },
      });
    } else {
      this.modelImageWraps.filter(modelImageWrap => {
        if (modelImageWrap.dataset.name === fileName) {
          modelImageWrap.classList.add(isActiveSelector);
        }
      });
      deferred.resolve();
    }

    return deferred;
  },

  /**
   * Reset measurement selection
   */
  resetMeasurementSelection() {
    const spotValues = this.sizeGuide.querySelectorAll('.js-spotValue');
    spotValues.forEach(spotValue => {
      spotValue.textContent = '...';

      const selectedRows = spotValue.querySelectorAll(isSelectedClass);
      if (selectedRows.length) {
        selectedRows.forEach(row => row.classList.remove(isSelectedSelector));
      }
    });

    const selectedSizeSelectLists = this.sizeGuide.querySelectorAll(
      `.js-sizeSelectLists${isSelectedClass}`
    );
    selectedSizeSelectLists.forEach(selectedSizeSelectList =>
      selectedSizeSelectList.classList.remove(isSelectedSelector)
    );
    this.hideSpotInfo();
  },

  /**
   * Select active measurements
   */
  selectActiveMeasurements() {
    const activeTables = this.tables.filter(table => table.classList.contains(isActiveSelector));

    if (!activeTables.length) {
      return false;
    }

    const sizeRangeValue = this.getKeyValue(SizeGuideKeys.SIZERANGE);
    if (sizeRangeValue) {
      this.selectElement(SizeGuideKeys.SIZERANGE, sizeRangeValue);
      this.setActiveKeyValue(SizeGuideKeys.SIZERANGE, sizeRangeValue);
    }

    activeTables.forEach(activeTable => {
      activeTable.classList.remove(isSelectedSelector);
      if (activeTable.classList.contains(`js-measurementsTable--${sizeRangeValue}`)) {
        activeTable.classList.add(isSelectedSelector);
      }
    });
  },

  /**
   * Select element
   */
  selectElement(key, value) {
    const filteredSelectionElements = [...this.selectionElements].filter(selectionElement =>
      selectionElement.matches(`[data-select^='${key}:']`)
    );
    filteredSelectionElements.forEach(filteredSelectionElement => {
      if (filteredSelectionElement.dataset.select === `${key}:${value}`) {
        filteredSelectionElement.classList.add(isSelectedSelector);
      } else {
        filteredSelectionElement.classList.remove(isSelectedSelector);
      }
    });
  },

  /**
   * Set value of active key
   */
  setActiveKeyValue(key, value) {
    activeSelection[key] = value;

    const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
    const guideValue = this.getKeyValue(SizeGuideKeys.GUIDE);

    if (genderValue && guideValue) {
      for (const _key in activeSelection) {
        if (activeSelection[_key]) {
          this.sizeGuide.setAttribute(`data-active-${_key}`, activeSelection[_key]);
        }
      }

      this.sizeSelections.forEach(sizeSelection =>
        sizeSelection.classList.remove(isHiddenSelector)
      );
      const mainView = this.sizeGuide.querySelector('.js-mainView');
      mainView.classList.remove(isHiddenSelector);
    }
  },

  /**
   * Show active tables
   */
  setActiveTables() {
    this.tables.forEach(table => {
      table.classList.remove(isActiveSelector);
      table.classList.remove(isSelectedSelector);
    });

    const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
    const guideValue = this.getKeyValue(SizeGuideKeys.GUIDE);
    const unitValue = this.getKeyValue(SizeGuideKeys.UNIT);

    if (genderValue && guideValue && unitValue) {
      this.tables.forEach(table => {
        if (
          table.classList.contains(`js-measurementsTable--${genderValue}`) &&
          table.classList.contains(`js-measurementsTable--${guideValue}`) &&
          table.classList.contains(`js-measurementsTable--${unitValue}`)
        ) {
          table.classList.add(isActiveSelector);
        }
      });
    }
  },

  handleAnalytics(event) {
    if (event?.target?.id === 'dialog-layer-size-guide') {
      document.jq.trigger(EventTypes.DIALOG_SIZE_GUIDE_SHOW);

      const sku = document.querySelector('.js-productDetailSection')?.dataset?.sku;

      if (!sku) return;

      document.jq.trigger(AnalyticsEventTypes.SIZE_GUIDE_OPEN, {
        materialNumber: sku,
      });
    }
  },

  /**
   * Set active keys as data on dialog
   */
  setData() {
    if (!this.sizeGuideTrigger) {
      return;
    }

    const sizeGuideObject = this.getParams(this.sizeGuideTrigger.href);
    const sizeGuideEntries = Object.entries(sizeGuideObject);
    sizeGuideEntries.forEach(([key, value]) => {
      this.selectElement(key, value);
      this.setActiveKeyValue(key, value);
    });

    this.updateCategory();
    this.updateSizeInfo();
    this.loadModelImage();
    this.highlightSelectedTableRow();
    this.displayMeasurementValuesOnModelImage(false, true);
  },

  /**
   * Set unit (cm/inch) depending on locale
   */
  setPreferredUnit() {
    const unitValue = AppSettings.sizeGuideUnit === 'inch' ? SizeGuideKeys.INCH : SizeGuideKeys.CM;
    $(`[data-select='${SizeGuideKeys.UNIT}:${unitValue}']`).trigger(
      device.hasTouch ? 'touch' : 'click'
    );
  },

  /**
   * Show info spot from table heading
   */
  showSpotFromTable(event) {
    event.stopPropagation();
    const $target = $(event.currentTarget);
    const $spotInfo = this.showSpotInfo(event, $target.attr('data-spot'));

    scrollPositionY = this.$dialog.length ? this.$dialog.scrollTop() : window.scrollY;

    if ($spotInfo.length > 0) {
      if (this.$dialog.length) {
        top = $spotInfo.closest('.js-mainView').offset().top + scrollPositionY;
        this.$dialog.scrollTop(top);
      } else {
        const topNavHeight = document.querySelector('.js-topNavigation').offsetHeight;
        top = $spotInfo.closest('.js-mainView').offset().top - topNavHeight;
        scrollTo(top);
      }
    }
  },

  /**
   * Show detailed description measurement spot
   */
  showSpotInfo(event, spot) {
    event.stopPropagation();
    spot = spot || $(event.currentTarget).attr('data-spot');

    const category = this.sizeGuide.getAttribute('data-active-guide');
    const modelMeasurementInfo = this.sizeGuide.querySelector(
      `.js-modelMeasurementInfo .sizeGuide-modelMeasurement-infoWrap--${category}`
    );

    const spotInfo = modelMeasurementInfo.querySelector(
      `.sizeGuide-modelMeasurement-spotInfo--${spot}`
    );
    const isSpotInfoActive = spotInfo.classList.contains(isActiveSelector);

    if (!isSpotInfoActive) {
      this.sizeGuide.classList.add('has-activeInfo');
      this.sizeGuide.setAttribute('data-active-spot', spot);
      const modelMeasurementInfoParagraphs = modelMeasurementInfo.querySelectorAll('p');
      if (modelMeasurementInfoParagraphs.length) {
        modelMeasurementInfoParagraphs.forEach(paragraph =>
          paragraph.classList.remove(isActiveSelector)
        );
      }
      spotInfo.classList.add(isActiveSelector);
    }

    return spotInfo;
  },

  /**
   * Set height navigation panel
   */
  setPanelHeight(panel) {
    const panelHeight = panel.classList.contains(isOpenedSelector)
      ? panel.dataset.opened
      : panel.dataset.closed;
    gsap.to(panel, { duration: 0.4, css: { height: panelHeight } });
  },

  /**
   * Set height data navigation panel
   */
  setPanelHeightData(panel) {
    let innerHeight = 0;
    [...panel.children].forEach(child => (innerHeight += this.getOuterHeightWithMargin(child)));
    panel.dataset.heightSet = true;
    panel.dataset.closed = `${panel.offsetHeight}px`;
    panel.dataset.opened = `${innerHeight}px`;
  },

  getOuterHeightWithMargin(element) {
    let height = element.offsetHeight;
    const style = getComputedStyle(element);
    height += parseInt(style.marginTop) + parseInt(style.marginBottom);
    return height;
  },

  /**
   * Toggle navigation panel
   */
  togglePanel(event) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    const navPanel = target.classList.contains(navPanelClass)
      ? target
      : target.closest(navPanelClass);
    const hasHeightSet = navPanel.dataset.heightSet;

    if (!hasHeightSet) {
      this.setPanelHeightData(navPanel);
    }

    navPanel.classList.toggle(isOpenedSelector);
    const isOpened = navPanel.classList.contains(isOpenedSelector);
    navPanel.setAttribute('aria-expanded', isOpened);

    const makeSelectionElements = navPanel.querySelectorAll(makeSelectionClass);
    if (makeSelectionElements.length) {
      if (isOpened) {
        // Make child links clickable when panel is open
        makeSelectionElements.forEach(makeSelectionElement =>
          makeSelectionElement.setAttribute('href', '#')
        );
      } else {
        // Make child links not clickable when panel is closed
        makeSelectionElements.forEach(makeSelectionElement =>
          makeSelectionElement.removeAttribute('href')
        );

        // Set focus back to panel
        navPanel.focus();
      }
    }

    this.setPanelHeight(navPanel);
  },

  /**
   * Translate specific label of info spot
   */
  translateSpotLabel(element) {
    const spotKey = element.dataset.spot;
    const label = window.spotLabelTranslations[spotKey] || spotKey;
    const spotLabel = element.querySelector('.js-spotLabel');
    spotLabel.textContent = label;
  },

  /**
   * Translate headings the table
   */
  translateTableHeadings() {
    this.tables.forEach(table => {
      Array.from(table.jq.find('tr:first td')).forEach(heading => {
        this.translateTableHeading(heading);
      });
    });
  },

  /**
   * Translate specific table heading
   */
  translateTableHeading(cell) {
    if (!cell) {
      return;
    }

    const spotKey = cell.textContent.toLowerCase();
    const label = window.spotLabelTranslations[spotKey] || spotKey;

    cell.setAttribute('data-spot', spotKey);
    cell.setAttribute('data-original-value', spotKey);
    cell.textContent = label;

    const modelMeasurementInfo = this.sizeGuide.querySelector(
      `.sizeGuide-modelMeasurement-spotInfo--${spotKey}`
    );
    if (modelMeasurementInfo) {
      const infoIcon = document.createElement('a');
      infoIcon.classList.add('js-spotInfoTrigger', 'measurementsInfoIcon');
      infoIcon.setAttribute('data-spot', spotKey);
      infoIcon.textContent = 'i';
      cell.appendChild(infoIcon);
    }
  },

  /**
   * Update category class
   */
  updateCategory() {
    const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
    const guideValue = this.getKeyValue(SizeGuideKeys.GUIDE);

    if (genderValue && guideValue) {
      const newCategory = `${genderValue}-${guideValue}`;

      if (this.category) {
        this.sizeGuide.classList.remove(`category-${this.category}`);
      }

      this.sizeGuide.classList.add(`category-${newCategory}`);
      this.category = newCategory;
    }

    this.updateCategoryTitle();
  },

  /**
   * Update category title
   */
  updateCategoryTitle() {
    const navPanels = this.sizeGuide.querySelectorAll(navPanelClass);
    navPanels.forEach(navPanel => {
      const selectedItem = navPanel.querySelector('.js-selectedItem');

      if (navPanel.classList.contains(`js-navPanel--${SizeGuideKeys.GENDER}`)) {
        const genderValue = this.getKeyValue(SizeGuideKeys.GENDER);
        const genderSelect = this.sizeGuide.querySelector(
          `[data-select='${SizeGuideKeys.GENDER}:${genderValue}']`
        );
        if (genderSelect) {
          selectedItem.textContent = genderSelect.textContent;
        }
      }

      if (navPanel.classList.contains('js-navPanel--category')) {
        const guideValue = this.getKeyValue(SizeGuideKeys.GUIDE);
        const guideSelect = this.sizeGuide.querySelector(
          `[data-select='${SizeGuideKeys.GUIDE}:${guideValue}']`
        );
        if (guideSelect) {
          selectedItem.textContent = guideSelect.textContent;
        }
      }
    });
  },

  /**
   * Toggle audience
   */
  toggleAudience(genderValue) {
    const categoryPanel = this.sizeGuide.querySelector('.js-navPanel--category');
    const activeGuide = categoryPanel.querySelector(
      `[data-select^="${SizeGuideKeys.GUIDE}:"].is-selected`
    );
    const sizeSelectionPanel = this.sizeGuide.querySelector('.js-navPanel--sizeSelection');
    const sizeGuideModelWrap = this.sizeGuide.querySelector('.js-sizeGuideModelWrap');
    const belts = categoryPanel.querySelector(`[data-select$=":${SizeGuideKeys.BELTS}"]`);
    const shoes = categoryPanel.querySelector(`[data-select$=":${SizeGuideKeys.SHOES}"]`);
    const caps = categoryPanel.querySelector(`[data-select$=":${SizeGuideKeys.CAPS}"]`);
    const isBaby = genderValue === SizeGuideKeys.BABIES;
    const isAdult =
      genderValue !== SizeGuideKeys.BOYS && genderValue !== SizeGuideKeys.GIRLS && !isBaby;

    belts.classList.toggle('is-disabled', !isAdult);
    shoes.classList.toggle('is-disabled', !isAdult);
    caps && caps.classList.toggle('is-disabled', !isBaby);
    sizeSelectionPanel.classList.toggle('is-disabled', !isAdult);
    sizeGuideModelWrap.classList.toggle('is-disabled', !isAdult);

    this.setPanelHeightData(categoryPanel);
    this.setPanelHeight(categoryPanel);

    if (!isAdult && activeGuide.classList.contains('is-disabled')) {
      this.selectElement(SizeGuideKeys.GUIDE, SizeGuideKeys.BOTTOMS);
    }
  },
});

export default Singleton.create(SizeGuide, 'SizeGuide');
